import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomerBillingCustomerModel } from '../customer-billing-customer.model';
import { CustomerBillingOtherCustomerModel } from '../../customer-billing-other-customer/customer-billing-other-customer.model';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { fiPaymentMethodType } from '../../payment-method-type';
import { BillingContactModel } from '@fi-app/billing/billing-contact.model';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import {
    fiTerminationPeriodTypes,
    ITerminationPeriod,
} from '../../customer-billing-components/termination-period-type-name.pipe';
import { CustomerService } from '../customer.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { fiPackageType, fiPackageTypes } from '@fi-app/customer-billing/customer-billing-customer/package-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';

@Component({
    selector: 'fi-customer-billing-customer-edit',
    templateUrl: './customer-billing-customer-edit.component.html',
    styleUrls: ['./customer-billing-customer-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-customer-billing-customer-edit' },
    standalone: false,
})
export class CustomerBillingCustomerEditComponent implements OnInit {
    @Input() customer: CustomerBillingCustomerModel;
    @Input() resellers: CustomerBillingOtherCustomerModel[];

    EditTabs: EditTabsModel;
    contractLengthListData = new DropdownSettingsModelV2<INameIdObj>();
    resellerListData = new DropdownSettingsModelV2<CustomerBillingOtherCustomerModel>();
    lowestUnitLevelChargeListData = new DropdownSettingsModelV2<INameIdObj>();
    BillingReference: BillingContactModel;
    CcBillingReference: BillingContactModel;

    constructor(
        private appTextService: AppTextService,
        private customerService: CustomerService,
        private fiLoadingService: LoadingService,
        private stateService: FiStateService,
        private objectTitleService: ObjectTitleService,
    ) {}

    ngOnInit() {
        this.setInitialListsData();
        this.setPageHeaderData();
    }

    private setInitialListsData() {
        this.contractLengthListData.setDataSet(
            fiTerminationPeriodTypes.map((type: ITerminationPeriod) => {
                return {
                    Id: type.Id,
                    Name: this.appTextService.get(type.TextId),
                };
            }),
        );

        this.resellerListData.setDataSet(this.resellers);

        this.BillingReference = this.customer.InvoiceInfo.BillingReference[0] || new BillingContactModel();
        this.CcBillingReference = this.customer.CreditCardInfo.BillingReference[0] || new BillingContactModel();

        const lowestUnitLevelCharges: INameIdObj[] = Object.keys(fiPackageTypes)
            .map((key: string) => {
                const value = fiPackageTypes[key];
                return {
                    Id: value.id,
                    Name: this.appTextService.get(value.textId),
                };
            })
            .filter((item) => item.Id !== fiPackageType.Free);
        this.lowestUnitLevelChargeListData.orderBy = 'Id';
        this.lowestUnitLevelChargeListData.setDataSet(lowestUnitLevelCharges);
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(this.getPageTitle());
        this.EditTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
    }

    private getPageTitle(): string {
        if (this.customer.CustomerId) return this.objectTitleService.getEditTitle(this.customer.Name);

        return this.objectTitleService.getCreateTitle(ObjectTypes.CustomerBillingCustomer);
    }

    save() {
        this.fiLoadingService.trackPromise(this.customerService.SaveCustomer(this.customer)).then(() => {
            this.stateService.go(FiStateNames.CustomerBilling.Customers.List);
        });
    }

    isCreditCard(): boolean {
        return this.customer.PaymentMethodType === fiPaymentMethodType.CreditCard;
    }

    isInvoice(): boolean {
        return this.customer.PaymentMethodType === fiPaymentMethodType.Invoice;
    }
}
